import { ComboboxInput } from '@headlessui/react';
import cx from 'classnames';
import { CSSProperties, ComponentPropsWithoutRef, forwardRef } from 'react';
import { useGlobalConfig } from '../../../hooks/configContext';

interface StyledAddressInputProps extends ComponentPropsWithoutRef<'input'> {
  open?: boolean;
  type?: string;
  displayValue?: (value: any) => string;
  version?: 'legacy' | 'v2025';
}

const StyledAddressInput = forwardRef<
  HTMLInputElement,
  StyledAddressInputProps
>(({ open, className = '', version = 'legacy', ...props }, ref) => {
  const { theme } = useGlobalConfig();

  /**
   * Tailwind token compatability/workaround:
   * To avoid different sizes between address search in storefront and breeze,
   * we're hard-coding pixel values.
   *
   * TODO: Once design tokens are unified across all apps, replace these classes with:
   * text-base sm:text-2xl
   */
  const fontSizeCompat =
    version === 'legacy'
      ? 'text-[16px] md:text-[20px] px-5 py-5 sm:px-12'
      : 'px-6 py-4 md:text-lg';

  return (
    <ComboboxInput
      ref={ref}
      className={cx(
        fontSizeCompat,
        `w-full appearance-none rounded-md border-none bg-white transition-all duration-200 ease-out outline-none disabled:cursor-not-allowed`,
        'bg-(--color-surface) text-(--color-heading) placeholder:text-(--color-body)/80 focus:bg-(--color-grey-1)',
        'shadow-[0_1px_2px_rgba(0,0,0,0.2),0_3px_6px_rgba(0,0,0,0.1)] focus:shadow-none',
        '[&::-webkit-input-placeholder]:!leading-normal', // Fix mobile Safari problem where placeholder isn't centered
        'placeholder:opacity-100', // Firefox fix
        open && 'rounded-br-none rounded-bl-none',
        className,
      )}
      style={
        {
          '--color-grey-1': theme.alias.background_200,
          '--color-body': theme.alias.text_200,
          '--tw-ring-color': `${theme.alias.border_100} !important`,
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
        } as CSSProperties
      }
      {...props}
    />
  );
});

StyledAddressInput.displayName = 'StyledAddressInput';

export default StyledAddressInput;
